import AWS from 'aws-sdk'
import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Stack
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import PersonIcon from '@mui/icons-material/Person'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import { useDispatch, useSelector } from 'react-redux'
import urls from '../../constants/apiurl'
import ApiCaller from '../../constants/ApiCaller'
import { showDangerToast, showToast } from '../../constants/toaster'
import { uplodateLoading } from '../../../redux/features/common/commonSlice'
import './SendUserNotificationModal.css'

const PRIMARY = '#5e72e4'

const uploadNotificationImage = async (assetFile, onProgress) => {
  const S3_BUCKET = 'praxo-dev-cloud-front'
  const REGION = 'ap-south-1'

  AWS.config.update({
    accessKeyId: 'AKIA4MTWJYJHQ6U6YWFK',
    secretAccessKey: '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'
  })

  const s3 = new AWS.S3({
    params: { Bucket: S3_BUCKET },
    region: REGION
  })

  const fileName = 'notification-images/' + assetFile?.name
  const params = {
    Bucket: S3_BUCKET,
    Key: fileName,
    Body: assetFile
  }

  await s3
    .putObject(params)
    .on('httpUploadProgress', evt => {
      if (onProgress) {
        onProgress(parseInt((evt.loaded * 100) / evt.total, 10))
      }
    })
    .promise()

  return 'https://dhwdwg726vpm7.cloudfront.net/' + fileName
}

export const SendUserNotificationModal = ({
  show,
  onHide,
  userId,
  userDisplayName
}) => {
  const dispatch = useDispatch()
  const userData = useSelector(state => state?.userData)
  const [notificationTitle, setNotificationTitle] = useState('')
  const [notificationBody, setNotificationBody] = useState('')
  const [imageFile, setImageFile] = useState(null)
  const [selectedImage, setSelectedImage] = useState(null)
  const [uploadPercentage, setUploadPercentage] = useState(0)
  const [errors, setErrors] = useState({ titleError: '', bodyError: '' })
  const [sending, setSending] = useState(false)

  const resetForm = () => {
    setNotificationTitle('')
    setNotificationBody('')
    setImageFile(null)
    setSelectedImage(null)
    setUploadPercentage(0)
    setErrors({ titleError: '', bodyError: '' })
  }

  const handleClose = () => {
    if (sending) return
    resetForm()
    onHide()
  }

  const handleImageChange = e => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      setSelectedImage(URL.createObjectURL(file))
    }
  }

  const validateFields = () => {
    const tempErrors = { titleError: '', bodyError: '' }
    let isValid = true

    if (!notificationTitle.trim()) {
      tempErrors.titleError = 'Notification title is required.'
      isValid = false
    }
    if (!notificationBody.trim()) {
      tempErrors.bodyError = 'Notification message is required.'
      isValid = false
    }

    setErrors(tempErrors)
    return isValid
  }

  const sendPushNotification = async () => {
    if (!userId) {
      showDangerToast('User id is missing.')
      return
    }
    if (!validateFields()) return

    try {
      setSending(true)
      let notificationImage = ''
      if (imageFile) {
        dispatch(uplodateLoading(true))
        notificationImage = await uploadNotificationImage(imageFile, setUploadPercentage)
      }

      const postJson = {
        notificationType: 'PARTICULAR_USER',
        title: notificationTitle.trim(),
        data: {
          type: 'ADMIN_NOTIFICATION',
          typeId: '1'
        },
        imageUrl: notificationImage || '',
        description: notificationBody.trim(),
        customerId: [String(userId)]
      }

      const res = await ApiCaller.post(
        postJson,
        urls.sendBatchPushNotification,
        userData?.access_token
      )
      const response = await res.response.json()

      if (response.status == 200) {
        showToast(response?.message || 'Notification sent successfully.')
        resetForm()
        onHide()
      } else {
        showDangerToast(response.message || 'Failed to send notification.')
      }
    } catch (error) {
      console.error(error)
      showDangerToast(error.message || 'Failed to send notification.')
    } finally {
      setSending(false)
      dispatch(uplodateLoading(false))
    }
  }

  const displayName = userDisplayName || 'this user'

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className="send-notification-dialog"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 0 2rem rgba(136, 152, 170, 0.2)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box className="send-notification-dialog__header">
          <Box display="flex" alignItems="center" gap={1.25} flex={1} minWidth={0}>
            <Box className="send-notification-dialog__header-icon">
              <NotificationsActiveIcon />
            </Box>
            <Box minWidth={0}>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                Send notification
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Push message to one user
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={sending}
            size="small"
            sx={{ color: 'inherit' }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Box
          className="send-notification-dialog__recipient"
          sx={{ mb: 2.5 }}
        >
          <PersonIcon sx={{ fontSize: 20, color: PRIMARY }} />
          <Box flex={1} minWidth={0}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
              Recipient
            </Typography>
            <Chip
              label={displayName}
              size="small"
              sx={{
                mt: 0.5,
                fontWeight: 600,
                bgcolor: `${PRIMARY}14`,
                color: PRIMARY,
                maxWidth: '100%',
                height: 'auto',
                '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 }
              }}
            />
          </Box>
        </Box>

        <Stack spacing={2}>
          <TextField
            label="Title"
            placeholder="Enter notification title"
            value={notificationTitle}
            onChange={e => setNotificationTitle(e.target.value)}
            disabled={sending}
            fullWidth
            error={Boolean(errors.titleError)}
            helperText={errors.titleError}
          />

          <TextField
            label="Message"
            placeholder="Enter notification message"
            value={notificationBody}
            onChange={e => setNotificationBody(e.target.value)}
            disabled={sending}
            fullWidth
            multiline
            minRows={4}
            error={Boolean(errors.bodyError)}
            helperText={errors.bodyError}
          />

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ display: 'block', mb: 1 }}
            >
              Image (optional)
            </Typography>
            <Box className="send-notification-dialog__file-zone">
              <ImageOutlinedIcon sx={{ color: 'text.secondary', mr: 1 }} />
              <Button
                component="label"
                variant="outlined"
                size="small"
                disabled={sending}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  borderColor: '#e9ecef',
                  color: 'text.secondary'
                }}
              >
                Choose image
                <input
                  type="file"
                  hidden
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleImageChange}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }} noWrap>
                {imageFile?.name || 'PNG, JPG, WEBP'}
              </Typography>
            </Box>

            {selectedImage && (
              <Box
                component="img"
                src={selectedImage}
                alt="Preview"
                className="send-notification-dialog__preview"
              />
            )}

            {uploadPercentage > 0 && uploadPercentage < 100 && (
              <Box sx={{ mt: 1.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={uploadPercentage}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: '#e9ecef',
                    '& .MuiLinearProgress-bar': { bgcolor: PRIMARY, borderRadius: 3 }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Uploading… {uploadPercentage}%
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          borderTop: '1px solid #e9ecef',
          bgcolor: '#f8f9fe'
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={sending}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#e9ecef',
            color: '#525f7f'
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={sendPushNotification}
          disabled={sending}
          startIcon={<NotificationsActiveIcon />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: PRIMARY,
            boxShadow: '0 4px 12px rgba(94, 114, 228, 0.35)',
            '&:hover': { bgcolor: '#4a5fc1' }
          }}
        >
          {sending ? 'Sending…' : 'Send notification'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
